/**
 * Collision-layout voor de maan-transit card-stacks (finetune-rondes 2–4,
 * spec `2026-07-06-hd-moon-transit-chart-design.md`): botsende stacks glijden
 * lángs de wielrand — ze krijgen een verschoven ringhoek, waarbij positie én
 * corner-hug-uitlijning meebewegen — in plaats van een rechte px-verschuiving
 * (die cards van het wiel liet loskomen of eróverheen duwde). Puur op
 * maat-data zodat de logica los van de DOM testbaar is; de card-laag meet
 * alleen de stack-afmetingen en voedt deze functie.
 */

import { degToRad, normalizeAngle } from '../mandala-geometry.utils';

/** Rechthoek in px, relatief t.o.v. de card-laag-host. */
export interface MoonTransitLayoutRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/** Ring waarop de stacks ankeren, in px van de card-laag-host. */
export interface MoonTransitRingLayout {
  readonly centerX: number;
  readonly centerY: number;
  readonly anchorRadius: number;
  /**
   * Minimale afstand (px) van het wielmiddelpunt tot elk punt van een
   * card-rect: de zichtbare wielrand + een kleine marge. Op diagonale hoeken
   * steekt de corner-hug-overhang anders tot tientallen px óver de rand
   * (finetune-ronde 5: cards overlappen het wiel nooit); de plaatsing schuift
   * de stack dan radiaal naar buiten tot hij vrij ligt.
   */
  readonly wheelClearanceRadius: number;
}

/** Eén te leggen stack (of vast obstakel) met zijn gemeten afmetingen. */
export interface MoonTransitStackLayoutItem {
  readonly gate: number;
  /** Gewenste schermhoek: de poort-midAngle (graden, CW vanaf boven). */
  readonly desiredAngle: number;
  readonly width: number;
  readonly height: number;
  /** Vast obstakel (bv. het maan-icoon): wordt ontweken maar schuift zelf nooit. */
  readonly fixed?: boolean;
  /** Expliciete rect voor vaste obstakels (die ankeren niet corner-hugged op de ring). */
  readonly rect?: MoonTransitLayoutRect;
}

/** Sentinel-`gate` voor de today-marker (het maan-icoon) als vast obstakel. */
export const MOON_TRANSIT_TODAY_OBSTACLE_GATE = -1;

/**
 * Herleidt uit een rect (host-relatief px) het {hoek, straal}-anker dat een
 * stapel van die maat corner-hugged op precies die plek plaatst: neem het
 * rect-centrum als bootstrap-schermhoek, construeer daaruit het ankerpunt
 * (fracties × maat) en herleid hoek + straal; twee iteraties (constructie +
 * één verfijning) volstaan — de fractie-afhankelijkheid van de hoek is zwak.
 * Gedeeld door de maan- én just-now-kaartlagen (tear-out-start van een sleep
 * en compensatie-ankers).
 */
export function moonTransitTearOutPlacement(
  rect: MoonTransitLayoutRect,
  centerPx: { readonly x: number; readonly y: number },
  unitsToPx: number,
): { placement: { angle: number; radiusUnits: number }; anchorPx: { x: number; y: number } } {
  const { left, top, width, height } = rect;
  const angleOf = (x: number, y: number) =>
    normalizeAngle((Math.atan2(y - centerPx.y, x - centerPx.x) * 180) / Math.PI + 90);
  let angle = angleOf(left + width / 2, top + height / 2);
  let anchorX = 0;
  let anchorY = 0;
  for (let i = 0; i < 2; i++) {
    const { fx, fy } = moonTransitAnchorFractions(angle);
    anchorX = left + fx * width;
    anchorY = top + fy * height;
    angle = angleOf(anchorX, anchorY);
  }
  const radiusUnits = Math.hypot(anchorX - centerPx.x, anchorY - centerPx.y) / unitsToPx;
  return { placement: { angle, radiusUnits }, anchorPx: { x: anchorX, y: anchorY } };
}

/**
 * Minimale vrije ruimte (px) tussen twee stacks op minstens één as. Bewust
 * klein (finetune-ronde 5): cards mogen dicht op elkaar staan — zeker als dat
 * ze dichter bij hun poort houdt — zolang ze elkaar maar net niet raken.
 */
export const MOON_TRANSIT_MIN_CARD_GAP_PX = 2;

const MAX_PASSES = 8;

/** Maximale hoekstap (graden) per botsings-correctie, tegen doorschieten. */
const MAX_ANGLE_STEP_DEG = 40;

/**
 * Een richtings-as-component moet minstens zo groot zijn om een overlap via
 * die as op te lossen; anders explodeert de benodigde verschuiving
 * ((overlap + marge) / component).
 */
const MIN_USABLE_AXIS_COMPONENT = 0.25;

/**
 * Anker-fractie-profielen per 45° (finetune-ronde 5): op de zuivere assen is
 * de aanliggende zíjde gecentreerd tegen de poort (boven 0° → onderzijde
 * gecentreerd: fx 0,5 / fy 1; rechts 90° → linkerzijde verticaal gecentreerd:
 * fx 0 / fy 0,5; enz.), in de diagonaalzones geldt de dichtstbijzijnde-hoek-
 * regel volledig (fx/fy exact 0 of 1), met lineaire overgangen ertussen —
 * "hoe verder van de as, hoe meer hoek".
 */
const ANCHOR_FX_PER_45_DEG: readonly number[] = [0.5, 0, 0, 0, 0.5, 1, 1, 1, 0.5];
const ANCHOR_FY_PER_45_DEG: readonly number[] = [1, 1, 0.5, 0, 0, 0, 0.5, 1, 1];

/**
 * Anker-fracties van de uitlijning: op welk punt (fractie van breedte/hoogte)
 * de stack zijn ringanker raakt, als functie van de schermhoek (0° = boven,
 * CW). Gedeeld tussen de render-transform en de analytische rect-berekening
 * hieronder, zodat beide exact overeenkomen.
 */
export function moonTransitAnchorFractions(angleDeg: number): { fx: number; fy: number } {
  const normalized = normalizeAngle(angleDeg);
  const segment = Math.floor(normalized / 45);
  const t = (normalized - segment * 45) / 45;
  const lerp = (anchors: readonly number[]) => anchors[segment] + (anchors[segment + 1] - anchors[segment]) * t;
  return { fx: lerp(ANCHOR_FX_PER_45_DEG), fy: lerp(ANCHOR_FY_PER_45_DEG) };
}

/**
 * Effectieve anker-straal (px) van een stack: de basis-straal, op diagonale
 * hoeken radiaal vergroot tot het rect volledig buiten
 * {@link MoonTransitRingLayout.wheelClearanceRadius} valt. De relatie is
 * monotoon; twee correctie-iteraties volstaan ruim.
 */
export function moonTransitStackAnchorRadius(
  width: number,
  height: number,
  angleDeg: number,
  ring: MoonTransitRingLayout,
): number {
  let radius = ring.anchorRadius;
  for (let i = 0; i < 3; i++) {
    const rect = moonTransitRectAtRadius(width, height, angleDeg, ring, radius);
    const distance = minDistanceToCenter(rect, ring);
    const deficit = ring.wheelClearanceRadius - distance;
    if (deficit <= 0.1) {
      break;
    }
    radius += deficit;
  }
  return radius;
}

/**
 * Analytische rect van een stack met gegeven maat, corner-hugged op de
 * ringhoek — inclusief de radiale uitwijk die het wiel vrijhoudt.
 */
export function moonTransitStackRectAt(
  width: number,
  height: number,
  angleDeg: number,
  ring: MoonTransitRingLayout,
): MoonTransitLayoutRect {
  return moonTransitRectAtRadius(width, height, angleDeg, ring, moonTransitStackAnchorRadius(width, height, angleDeg, ring));
}

/**
 * Analytische rect van een stack op een expliciete anker-straal (px),
 * corner-hugged op de ringhoek. Gedeeld met de card-laag: een gepinde stack
 * bepaalt zijn eigen straal (i.p.v. de wiel-vrijwarende auto-straal) en levert
 * die hier direct aan, zowel voor het renderen als voor de obstakel-rect in de
 * collision-resolver. De hoek-conventie is die van {@link polarToCartesian}
 * (0° = boven, met de klok mee → `angleDeg − 90` in radialen).
 */
export function moonTransitRectAtRadius(
  width: number,
  height: number,
  angleDeg: number,
  ring: MoonTransitRingLayout,
  radius: number,
): MoonTransitLayoutRect {
  const rad = degToRad(angleDeg - 90);
  const anchorX = ring.centerX + radius * Math.cos(rad);
  const anchorY = ring.centerY + radius * Math.sin(rad);
  const { fx, fy } = moonTransitAnchorFractions(angleDeg);
  return { left: anchorX - fx * width, top: anchorY - fy * height, width, height };
}

/** Kleinste afstand van het wielmiddelpunt tot (een punt van) het rect. */
function minDistanceToCenter(rect: MoonTransitLayoutRect, ring: MoonTransitRingLayout): number {
  const dx = Math.max(rect.left - ring.centerX, 0, ring.centerX - (rect.left + rect.width));
  const dy = Math.max(rect.top - ring.centerY, 0, ring.centerY - (rect.top + rect.height));
  return Math.hypot(dx, dy);
}

interface ResolveState {
  readonly item: MoonTransitStackLayoutItem;
  angle: number;
  /**
   * Vergrendelde draairichting (+1 = met de klok mee, −1 = tegen, 0 = nog
   * vrij): eenmaal weggeduwd blijft een stack dezelfde kant op draaien.
   * Zonder dit slot kan een stack in een keten (A ← B → C) heen-en-weer
   * pingpongen tussen zijn buren en raakt de botsing nooit opgelost.
   */
  direction: 0 | 1 | -1;
}

function rectOf(state: ResolveState, ring: MoonTransitRingLayout): MoonTransitLayoutRect {
  if (state.item.rect != null) {
    return state.item.rect;
  }
  return moonTransitStackRectAt(state.item.width, state.item.height, state.angle, ring);
}

/** Kleinste getekende hoekafstand a−b, genormaliseerd naar (−180, 180]. */
function signedAngleDelta(a: number, b: number): number {
  const delta = normalizeAngle(a - b);
  return delta > 180 ? delta - 360 : delta;
}

/**
 * Wie wijkt bij een botsing (finetune-ronde 5): een vast obstakel nooit;
 * daarna de stack die al van zijn poort weggeschoven is (die schuift verder
 * "met het wiel mee", de ander blijft op zijn poort staan); bij twee
 * onverplaatste stacks de lichtste (kleinste oppervlakte — een meervoudige
 * poort-stack heeft meer recht op zijn plek); als laatste de latere in
 * ringvolgorde.
 */
function pickMover(earlier: ResolveState, later: ResolveState): ResolveState {
  if (earlier.item.fixed) {
    return later;
  }
  if (later.item.fixed) {
    return earlier;
  }
  const earlierDisplacement = Math.abs(signedAngleDelta(earlier.angle, earlier.item.desiredAngle));
  const laterDisplacement = Math.abs(signedAngleDelta(later.angle, later.item.desiredAngle));
  if (Math.abs(earlierDisplacement - laterDisplacement) > 1e-6) {
    return earlierDisplacement > laterDisplacement ? earlier : later;
  }
  const earlierArea = earlier.item.width * earlier.item.height;
  const laterArea = later.item.width * later.item.height;
  if (earlierArea !== laterArea) {
    return earlierArea < laterArea ? earlier : later;
  }
  return later;
}

/**
 * Legt de stacks rond de ring zonder onderlinge botsingen: bij overlap (of
 * minder dan {@link MOON_TRANSIT_MIN_CARD_GAP_PX} vrije ruimte) draait de
 * wijkende stack ({@link pickMover}: al-verplaatste of lichtste eerst, vaste
 * obstakels nooit) van de ander weg — "met de mandala mee" — precies ver
 * genoeg om op minstens één as de marge vrij te maken. Meerdere passes vangen
 * kettingbotsingen op; het richtingslot houdt elke stack monotoon op koers.
 *
 * @returns per verschoven poort de opgeloste ringhoek (graden); poorten die
 * op hun gewenste hoek blijven, ontbreken in de map.
 */
export function resolveMoonTransitStackAngles(
  items: readonly MoonTransitStackLayoutItem[],
  ring: MoonTransitRingLayout,
): Map<number, number> {
  // Vaste obstakels eerst, daarna stacks in ringvolgorde: deterministisch,
  // onafhankelijk van de aanlever-volgorde van de formaties.
  const states: ResolveState[] = [...items]
    .sort((a, b) => {
      if ((a.fixed ?? false) !== (b.fixed ?? false)) {
        return a.fixed ? -1 : 1;
      }
      return a.desiredAngle - b.desiredAngle || a.gate - b.gate;
    })
    .map(item => ({ item, angle: item.desiredAngle, direction: 0 as const }));

  for (let pass = 0; pass < MAX_PASSES; pass++) {
    let moved = false;
    for (let i = 1; i < states.length; i++) {
      for (let j = 0; j < i; j++) {
        const earlier = states[j];
        const later = states[i];
        if (earlier.item.fixed && later.item.fixed) {
          continue;
        }
        const earlierRect = rectOf(earlier, ring);
        const laterRect = rectOf(later, ring);
        // Overlap per as; negatief = al vrije ruimte. Pas klaar wanneer er op
        // minstens één as de minimale marge vrij is.
        const overlapX = Math.min(earlierRect.left + earlierRect.width, laterRect.left + laterRect.width)
          - Math.max(earlierRect.left, laterRect.left);
        const overlapY = Math.min(earlierRect.top + earlierRect.height, laterRect.top + laterRect.height)
          - Math.max(earlierRect.top, laterRect.top);
        if (overlapX <= -MOON_TRANSIT_MIN_CARD_GAP_PX || overlapY <= -MOON_TRANSIT_MIN_CARD_GAP_PX) {
          continue;
        }

        const mover = pickMover(earlier, later);
        const other = mover === later ? earlier : later;

        // Draairichting: bij de eerste duw van de ander weg (kortste getekende
        // hoekafstand); daarna vergrendeld zodat de stack monotoon dezelfde
        // kant op blijft draaien en ketens niet oscilleren.
        const delta = signedAngleDelta(mover.angle, other.angle);
        const sign = mover.direction !== 0
          ? mover.direction
          : (delta !== 0 ? Math.sign(delta) : (mover === later ? 1 : -1));
        mover.direction = sign as 1 | -1;

        // Bewegingsrichting in px bij +sign·dθ: de ring-tangent op de huidige
        // hoek (positie (sinθ, −cosθ) → d/dθ = (cosθ, sinθ)).
        const rad = degToRad(mover.angle);
        const directionX = sign * Math.cos(rad);
        const directionY = sign * Math.sin(rad);

        // Ruwe bovengrens voor de boogafstand via de as-projecties (zonder
        // bruikbare as: de kleinste overlap als startgok), daarna bisectie
        // naar de kléínste boog die de botsing oplost — zo stopt elke card zo
        // dicht mogelijk tegen zijn buur/poort i.p.v. door te schieten
        // (finetune-ronde 5).
        const moverRect = mover === later ? laterRect : earlierRect;
        const otherRect = mover === later ? earlierRect : laterRect;
        const separationX = (moverRect.left + moverRect.width / 2) - (otherRect.left + otherRect.width / 2);
        const separationY = (moverRect.top + moverRect.height / 2) - (otherRect.top + otherRect.height / 2);
        const candidates: number[] = [];
        if (directionX * separationX > 0 && Math.abs(directionX) >= MIN_USABLE_AXIS_COMPONENT) {
          candidates.push((overlapX + MOON_TRANSIT_MIN_CARD_GAP_PX) / Math.abs(directionX));
        }
        if (directionY * separationY > 0 && Math.abs(directionY) >= MIN_USABLE_AXIS_COMPONENT) {
          candidates.push((overlapY + MOON_TRANSIT_MIN_CARD_GAP_PX) / Math.abs(directionY));
        }
        const roughPx = candidates.length > 0
          ? Math.min(...candidates)
          : Math.min(overlapX, overlapY) + MOON_TRANSIT_MIN_CARD_GAP_PX;

        const clearsAt = (deg: number): boolean => {
          const trial = moonTransitStackRectAt(mover.item.width, mover.item.height, mover.angle + sign * deg, ring);
          const trialOverlapX = Math.min(otherRect.left + otherRect.width, trial.left + trial.width)
            - Math.max(otherRect.left, trial.left);
          const trialOverlapY = Math.min(otherRect.top + otherRect.height, trial.top + trial.height)
            - Math.max(otherRect.top, trial.top);
          return trialOverlapX <= -MOON_TRANSIT_MIN_CARD_GAP_PX || trialOverlapY <= -MOON_TRANSIT_MIN_CARD_GAP_PX;
        };

        let stepDeg = Math.min(
          Math.max((roughPx / ring.anchorRadius) * (180 / Math.PI), 0.5),
          MAX_ANGLE_STEP_DEG,
        );
        while (!clearsAt(stepDeg) && stepDeg < MAX_ANGLE_STEP_DEG) {
          stepDeg = Math.min(stepDeg * 2, MAX_ANGLE_STEP_DEG);
        }
        if (clearsAt(stepDeg)) {
          let lowerBound = 0;
          for (let iteration = 0; iteration < 8; iteration++) {
            const mid = (lowerBound + stepDeg) / 2;
            if (clearsAt(mid)) {
              stepDeg = mid;
            } else {
              lowerBound = mid;
            }
          }
        }
        mover.angle += sign * stepDeg;
        moved = true;
      }
    }
    if (!moved) {
      break;
    }
  }

  const resolved = new Map<number, number>();
  for (const state of states) {
    if (!state.item.fixed && state.angle !== state.item.desiredAngle) {
      resolved.set(state.item.gate, normalizeAngle(state.angle));
    }
  }
  return resolved;
}
